import i18nFactory, { i18nRaw as i18nRawFactory } from '@yandex-int/i18n';
import classNames from 'classnames';
import { Dropdown, Menu } from 'lego-on-react';
import * as React from 'react';
import Linkify from 'react-linkify';

import { Bem } from '../../service/Bem/Bem';
import { UrlHelpers } from '../../service/UrlHelpers';
import { Lang, LinkTarget, Tld } from '../../typings/common';
import { AdvType } from '../../typings/domain';
import {
    ModerationRejectKey,
    ModerationRejectReason
} from '../../typings/moderation';
import { ButtonDecorator } from '../ButtonDecorator/ButtonDecorator';
import { Collapser } from '../Collapser/Collapser';
import { Shape2, Shape2Icon, Shape2Theme } from '../Shape2/Shape2';
import { StyledLink, StyledLinkTheme } from '../StyledLink/StyledLink';

import './ModerationRejectReasonDescription.css';
import * as keyset from './ModerationRejectReasonDescription.i18n';

const i18n = i18nFactory(keyset);
const i18nRaw = i18nRawFactory(keyset);

interface ModerationRejectReasonDescriptionProps {
    cls?: string;
    bid?: string;
    bannerType?: AdvType;
    reason: ModerationRejectReason;
    recommendationLinks?: Array<string>;
    lang: Lang;
    tld: Tld;
    contactEmail?: string;
    guaranteeLetterLinks?: Array<string>;
    isOperatorAgency: boolean;
    clientRegion: string;
}

export class ModerationRejectReasonDescription extends React.PureComponent<
    ModerationRejectReasonDescriptionProps
> {
    render() {
        const { cls } = this.props;
        const content = this.getModerationRejectReasonData();

        return (
            <div
                className={classNames(
                    cls,
                    Bem.cls('moderation-reject-reason-description')
                )}
            >
                {content}
            </div>
        );
    }

    private getModerationRejectReasonData = () => {
        const {
            bannerType,
            lang,
            tld,
            bid,
            contactEmail,
            isOperatorAgency,
            clientRegion
        } = this.props;

        switch (this.props.reason.key) {
            case 'other':
                return this.props.reason.recommendations.map(
                    (recommendation, index) => {
                        return (
                            <p
                                key={index}
                                className={Bem.cls(
                                    'moderation-reject-reason-description',
                                    'par'
                                )}
                            >
                                <Linkify
                                    component={StyledLink}
                                    properties={{
                                        theme: StyledLinkTheme.blue,
                                        target: LinkTarget.blank
                                    }}
                                >
                                    {recommendation}
                                </Linkify>
                            </p>
                        );
                    }
                );

            case ModerationRejectKey.kazakhLaw:
                return (
                    <>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('nelzya-reklamirovat')}
                        </p>
                        <ul
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'list'
                            )}
                        >
                            <li>{i18n('recepturnye-preparaty')}</li>
                            <li>{i18n('tabak-i-nbsp-tabachnye')}</li>
                            <li>
                                {i18n(
                                    'narkoticheskie-psihotropnye-veshchestva-i'
                                )}
                            </li>
                            <li>{i18n('azartnye-igry')}</li>
                            <li>{i18n('oruzhie')}</li>
                            <li>
                                {i18nRaw('drugie-other-tovary-i', {
                                    other: (
                                        <StyledLink
                                            theme={StyledLinkTheme.blue}
                                            href={
                                                getModerationHelpLinks(
                                                    lang,
                                                    tld
                                                ).restrictedGoodsInKz
                                            }
                                            target={LinkTarget.blank}
                                        >
                                            {i18n(
                                                'drugie-other-tovary-i-param-other'
                                            )}
                                        </StyledLink>
                                    )
                                })}
                            </li>
                        </ul>
                    </>
                );

            case ModerationRejectKey.copyright:
                return (
                    <>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('pri-reklame-rezultatov-intellektualnoy')}
                        </p>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {getInfoText(
                                i18n('s-pomoshchyu-garantiynogo-pisma')
                            )}
                        </p>
                        {getDownloadGuaranteeLetterButton(
                            isOperatorAgency,
                            bid
                        )}
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18nRaw('pered-zapolneniem-garantiynogo-pisma', {
                                rules: (
                                    <StyledLink
                                        theme={StyledLinkTheme.blue}
                                        href={
                                            getModerationHelpLinks(lang, tld)
                                                .guarantyLetter
                                        }
                                        target={LinkTarget.blank}
                                    >
                                        {i18n(
                                            'pered-zapolneniem-garantiynogo-pisma-param-rules'
                                        )}
                                    </StyledLink>
                                )
                            })}
                        </p>
                    </>
                );

            case ModerationRejectKey.spam:
                return (
                    <>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18nRaw('v-hode-proverki-byli', {
                                rules: (
                                    <StyledLink
                                        theme={StyledLinkTheme.blue}
                                        href={
                                            getModerationHelpLinks(lang, tld)
                                                .generalAdvRules
                                        }
                                        target={LinkTarget.blank}
                                    >
                                        {i18n(
                                            'v-hode-proverki-byli-param-rules'
                                        )}
                                    </StyledLink>
                                )
                            })}
                        </p>
                    </>
                );

            case ModerationRejectKey.russianLaw:
                return (
                    <>
                        {getRussianLawRecommendation(lang, tld)}
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {getInfoText(i18n('yazyk-sayta-i-nbsp-101'))}
                        </p>
                    </>
                );

            case ModerationRejectKey.russianLawCallout:
                return getRussianLawRecommendation(lang, tld);

            case ModerationRejectKey.yandexPolicy:
                return (
                    <>
                        <ul
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'list'
                            )}
                        >
                            <li>
                                {i18n('yandex-policy-title')}

                                <ul
                                    className={Bem.cls(
                                        'moderation-reject-reason-description',
                                        'no-markers-list'
                                    )}
                                >
                                    <li>{i18n('kak-zarabotat-v-nbsp')}</li>
                                    <li>{i18n('kak-zarabotat-v-nbsp-130')}</li>
                                </ul>
                            </li>
                            <li>
                                {i18n(
                                    'stranica-perehoda-dolzhna-sootvetstvovat-103'
                                )}
                            </li>
                            <li>{i18n('na-stranice-perehoda-razmeshcheny')}</li>
                            <li>{i18n('v-tekste-obyavleniya-est')}</li>
                            <li>{i18n('na-sayte-otsutstvuyut-virusy')}</li>
                            <li>{i18n('na-stranice-perehoda-ukazana')}</li>
                        </ul>

                        {getGeneralAdvRulesLink(lang, tld)}
                    </>
                );

            case ModerationRejectKey.siteRequirements:
                return (
                    <>
                        <ul
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'list'
                            )}
                        >
                            <li>
                                {i18n('na-stranice-perehoda-prisutstvuet-104')}
                            </li>
                            <li>
                                {i18n(
                                    'stranica-perehoda-otkryvaetsya-korrektno'
                                )}
                            </li>
                            <li>{i18n('reklamnye-bloki-na-nbsp')}</li>
                            <li>{i18n('na-stranice-perehoda-otsutstvuyut')}</li>
                            <li>{i18n('sayt-sostavlen-na-nbsp-105')}</li>
                        </ul>

                        {getGeneralAdvRulesLink(lang, tld)}
                    </>
                );

            case ModerationRejectKey.newsRequirements:
                return (
                    <>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('vy-mozhete-ispolzovat-utochnyayushchie')}
                        </p>

                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {getInfoText(i18n('ubedites-chto-v-nbsp-106'))}
                        </p>
                    </>
                );

            case ModerationRejectKey.falseInfo:
                return (
                    <>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('slova-v-nbsp-prevoshodnoy-109')}
                        </p>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('esli-u-vas-net')}
                        </p>
                    </>
                );

            case ModerationRejectKey.errors:
                return (
                    <>
                        <h3
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'sub-title'
                            )}
                        >
                            {i18n('v-tekste-obyavleniya-ne')}:
                        </h3>

                        <ul
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'list'
                            )}
                        >
                            {bannerType === AdvType.CPC_VIDEO ? (
                                <li>
                                    {i18n('pisat-video-imena-sobstvennye-so')}
                                </li>
                            ) : (
                                <>
                                    <li>
                                        {i18n('pisat-imena-sobstvennye-so')}
                                    </li>
                                    <li>{i18n('ispolzovat-r-nbsp-a')}</li>
                                    <li>
                                        {i18n('pisat-slova-zaglavnymi-bukvami')}
                                    </li>
                                </>
                            )}
                            <li>{i18n('ispolzovat-zhargon-i-nbsp')}</li>
                            <li>
                                {i18n('ispolzovat-nekorrektnye-sokrashcheniya')}
                            </li>
                            <li>
                                {i18n('ispolzovat-mnogokratnye-povtory-odnih')}
                            </li>
                        </ul>

                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            <StyledLink
                                theme={StyledLinkTheme.blue}
                                href={
                                    getModerationHelpLinks(lang, tld).advRules
                                }
                                target={LinkTarget.blank}
                            >
                                {i18n('polnyy-spisok-trebovaniy')}
                            </StyledLink>
                        </p>

                        {bannerType &&
                        [
                            AdvType.MCBANNER,
                            AdvType.CPM_BANNER,
                            AdvType.CPM_VIDEO
                        ].includes(bannerType)
                            ? null
                            : i18n('proverte-chtoby-pri-podstanovke')}
                    </>
                );

            case ModerationRejectKey.tm:
                return getTmReccommendation(
                    lang,
                    tld,
                    isOperatorAgency,
                    bid,
                    contactEmail
                );

            case ModerationRejectKey.invalidUrl:
                return (
                    <p
                        className={Bem.cls(
                            'moderation-reject-reason-description',
                            'par'
                        )}
                    >
                        {i18n('ubedites-chto-sayt-ispolzuemyy-112')}
                    </p>
                );

            case ModerationRejectKey.politics:
                return (
                    <>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('eto-kasaetsya-ne-nbsp')}
                        </p>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('my-takzhe-ne-nbsp')}
                        </p>
                    </>
                );

            case ModerationRejectKey.contactInfo:
                return (
                    <p
                        className={Bem.cls(
                            'moderation-reject-reason-description',
                            'par'
                        )}
                    >
                        {i18nRaw('v-obyavlenii-ne-nbsp', {
                            vcard: (
                                <StyledLink
                                    theme={StyledLinkTheme.blue}
                                    href={
                                        getModerationHelpLinks(lang, tld)
                                            .vcardRules
                                    }
                                    target={LinkTarget.blank}
                                >
                                    {i18n('v-obyavlenii-ne-nbsp-param-vcard')}
                                </StyledLink>
                            )
                        })}
                    </p>
                );

            case ModerationRejectKey.licenseRequired:
                return (
                    <>
                        {getLicenseHelpText(lang, tld)}
                        {getGuaranteeLetterRecommendation(
                            lang,
                            tld,
                            isOperatorAgency,
                            clientRegion,
                            bid
                        )}
                        {documentsTimeHelp}
                    </>
                );

            case ModerationRejectKey.irrelevantKeywords:
                return (
                    <>
                        <ul
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'list'
                            )}
                        >
                            <li>{i18n('klyuchevye-frazy-dolzhny-imet-113')}</li>
                            <li>{i18n('klyuchevye-frazy-ne-nbsp')}</li>
                        </ul>

                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {getInfoText(
                                <StyledLink
                                    theme={StyledLinkTheme.blue}
                                    href={
                                        getModerationHelpLinks(lang, tld)
                                            .chooseKeywordsInfo
                                    }
                                    target={LinkTarget.blank}
                                >
                                    {i18n('kak-naibolee-effektivno-podobrat')}
                                </StyledLink>
                            )}
                        </p>
                    </>
                );

            case ModerationRejectKey.russianLawVcard:
                return getRussianLawReccomendation(lang, tld);

            case ModerationRejectKey.licenseRequiredVcard:
                return (
                    <>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('podrobnoe-opisanie-obekta-reklamirovaniya')}
                        </p>
                        {getLicenseHelpText(lang, tld)}
                        {getGuaranteeLetterRecommendation(
                            lang,
                            tld,
                            isOperatorAgency,
                            clientRegion,
                            bid
                        )}
                        {documentsTimeHelp}
                    </>
                );

            case ModerationRejectKey.falseInfoVcard:
                return (
                    <>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('vy-mozhete-ispolzovat-slova')}
                        </p>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('esli-u-vas-net-132')}
                        </p>
                    </>
                );

            case ModerationRejectKey.phoneDoesntAnswer:
                return (
                    <>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('my-ne-nbsp-smogli')}
                        </p>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('proverte-pravilno-nbsp-li')}
                        </p>
                    </>
                );

            case ModerationRejectKey.invalidContacts:
                return (
                    <>
                        <ul
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'list'
                            )}
                        >
                            <li>
                                {i18n('telefon-dolzhen-byt-propisan')}
                                &nbsp;
                                {i18n('primer-7-916-123')}
                            </li>
                            <li>{i18n('adres-nazvanie-organizacii-i')}</li>
                        </ul>
                    </>
                );

            case ModerationRejectKey.yandexPolicyAuto:
                return (
                    <>
                        <ul
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'list'
                            )}
                        >
                            <li>{i18n('vashe-reklamnoe-obyavlenie-ne')}</li>
                            <li>
                                {i18nRaw('c-pravilami-razmeshcheniya-reklamy', {
                                    link: (
                                        <StyledLink
                                            theme={StyledLinkTheme.blue}
                                            href={
                                                getModerationHelpLinks(
                                                    lang,
                                                    tld
                                                ).advRules
                                            }
                                            target={LinkTarget.blank}
                                        >
                                            {i18n(
                                                'c-pravilami-razmeshcheniya-reklamy-param-link'
                                            )}
                                        </StyledLink>
                                    )
                                })}
                            </li>
                        </ul>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            <StyledLink
                                theme={StyledLinkTheme.blue}
                                href={
                                    getModerationHelpLinks(lang, tld).errorBan
                                }
                                target={LinkTarget.blank}
                            >
                                {i18n(
                                    'link-obekta-reklamirovaniya-net-param-link'
                                )}
                            </StyledLink>
                        </p>
                    </>
                );

            case ModerationRejectKey.yandexPolicyVcard:
                return (
                    <>
                        <ul
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'list'
                            )}
                        >
                            <li>
                                {i18n('v-tekste-obyavleniya-chyotko')}
                                &nbsp;
                                {i18n('naprimer')}:
                                <ul
                                    className={Bem.cls(
                                        'moderation-reject-reason-description',
                                        'no-markers-list'
                                    )}
                                >
                                    <li>{i18n('kak-zarabotat-v-nbsp')}</li>
                                    <li>{i18n('kak-zarabotat-v-nbsp-134')}</li>
                                </ul>
                            </li>
                            <li>{i18n('esli-v-nbsp-obyavlenii')}</li>
                        </ul>
                        {getGeneralAdvRulesLink(lang, tld)}
                    </>
                );

            case ModerationRejectKey.blacklist:
                return (
                    <>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('my-ne-nbsp-smogli-135')}
                        </p>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('vnesite-neobhodimye-izmeneniya-i')}
                        </p>
                    </>
                );

            case ModerationRejectKey.errorsVcard:
                return (
                    <>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('ne-dopuskaetsya')}:
                        </p>
                        <ul
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'list'
                            )}
                        >
                            <li>{i18n('pisat-imena-sobstvennye-so')}</li>
                            <li>{i18n('ispolzovat-r-nbsp-a-137')}</li>
                            <li>{i18n('pisat-slova-zaglavnymi-bukvami')}</li>
                            <li>
                                {i18n('ispolzovat-nekorrektnye-sokrashcheniya')}
                            </li>
                            <li>
                                {i18n('ispolzovat-mnogokratnye-povtory-odnih')}
                            </li>
                        </ul>
                        {getGeneralAdvRulesLink(lang, tld)}
                    </>
                );

            case ModerationRejectKey.irrelevantImage:
                return (
                    <>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('dopustimo-ispolzovat-tolko-te-115')}
                        </p>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            <StyledLink
                                theme={StyledLinkTheme.blue}
                                href={
                                    getModerationHelpLinks(lang, tld)
                                        .directAdvRules
                                }
                                target={LinkTarget.blank}
                            >
                                {i18n('polnyy-spisok-trebovaniy-k')}
                            </StyledLink>
                        </p>
                    </>
                );

            case ModerationRejectKey.yandexPolicyImage:
                return (
                    <>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('izobrazhenie-ne-nbsp-dolzhno')}
                        </p>

                        <ul
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'list'
                            )}
                        >
                            <li>
                                {i18n('kontaktnuyu-informaciyu-naprimer-nomer')}
                            </li>
                            <li>
                                {i18n('odnotonnuyu-zalivku-tekst-logotipy')}
                            </li>
                            <li>{i18n('elementy-interfeysa-kotorye-mogut')}</li>
                            <li>
                                {i18n(
                                    'slishkom-otkrovennye-illyustracii-odezhdy'
                                )}
                            </li>
                            <li>
                                {i18n(
                                    'izobrazheniya-kotorye-ustoychivo-associiruyutsya'
                                )}
                            </li>
                            <li>{i18n('intimnye-foto-foto-intimnyh')}</li>
                        </ul>

                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {getInfoText(
                                i18nRaw('etot-spisok-ne-nbsp', {
                                    rules: (
                                        <StyledLink
                                            theme={StyledLinkTheme.blue}
                                            href={
                                                getModerationHelpLinks(
                                                    lang,
                                                    tld
                                                ).directAdvRules
                                            }
                                            target={LinkTarget.blank}
                                        >
                                            {i18n(
                                                'etot-spisok-ne-nbsp-param-rules'
                                            )}
                                        </StyledLink>
                                    )
                                })
                            )}
                        </p>
                    </>
                );

            case ModerationRejectKey.poorImage:
                return (
                    <p
                        className={Bem.cls(
                            'moderation-reject-reason-description',
                            'par'
                        )}
                    >
                        {i18n('kachestvo-zagruzhennoy-kartinki-ochen')}
                    </p>
                );

            case ModerationRejectKey.russianLawImage:
                return (
                    <>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('nelzya-reklamirovat')}
                        </p>

                        <ul
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'list'
                            )}
                        >
                            <li>
                                {i18n('azartnye-igry-sportivnye-prognozy-139')}
                            </li>
                            <li>{i18n('alkogolnuyu-produkciyu-140')}</li>
                            <li>{i18n('tabachnuyu-produkciyu-i-nbsp-141')}</li>
                            <li>{i18n('oruzhie-142')}</li>
                            <li>
                                {i18nRaw('drugie-rules-tovary-i', {
                                    rules: (
                                        <StyledLink
                                            theme={StyledLinkTheme.blue}
                                            href={
                                                getModerationHelpLinks(
                                                    lang,
                                                    tld
                                                ).advRules
                                            }
                                            target={LinkTarget.blank}
                                        >
                                            {i18n(
                                                'drugie-rules-tovary-i-param-rules'
                                            )}
                                        </StyledLink>
                                    )
                                })}
                            </li>
                        </ul>
                    </>
                );

            case ModerationRejectKey.russianLawQl:
                return getRussianLawReccomendation(lang, tld);

            case ModerationRejectKey.qlRequirements:
                return (
                    <>
                        <ul
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'list'
                            )}
                        >
                            <li>
                                {i18n('na-stranice-perehoda-prisutstvuet-143')}
                            </li>
                            <li>
                                {i18n(
                                    'stranica-perehoda-otkryvaetsya-korrektno'
                                )}
                            </li>
                            <li>{i18n('reklamnye-bloki-na-nbsp')}</li>
                            <li>{i18n('bystrye-ssylki-ne-nbsp')}</li>
                            <li>{i18n('na-stranice-perehoda-otsutstvuyut')}</li>
                            <li>{i18n('sayt-predstavlen-na-nbsp')}</li>
                        </ul>
                        {getGeneralAdvRulesLink(lang, tld)}
                    </>
                );

            case ModerationRejectKey.errorsQl:
                return (
                    <>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('ne-dopuskaetsya')}:
                        </p>

                        <ul
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'list'
                            )}
                        >
                            <li>{i18n('pisat-imena-sobstvennye-so-145')}</li>
                            <li>{i18n('ispolzovat-r-nbsp-a')}</li>
                            <li>{i18n('pisat-slova-zaglavnymi-bukvami')}</li>
                            <li>
                                {i18n('ispolzovat-nekorrektnye-sokrashcheniya')}
                            </li>
                            <li>
                                {i18n('ispolzovat-mnogokratnye-povtory-odnih')}
                            </li>
                        </ul>

                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            <StyledLink
                                theme={StyledLinkTheme.blue}
                                href={
                                    getModerationHelpLinks(lang, tld)
                                        .quickLinksRules
                                }
                                target={LinkTarget.blank}
                            >
                                {i18n('polnyy-spisok-trebovaniy')}
                            </StyledLink>
                        </p>
                    </>
                );

            case ModerationRejectKey.invalidUrlQl:
                return (
                    <p
                        className={Bem.cls(
                            'moderation-reject-reason-description',
                            'par'
                        )}
                    >
                        {i18n('pri-proverke-odna-ili')}
                    </p>
                );

            case ModerationRejectKey.urlToOtherDomain:
                return (
                    <>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('ubedites-chto-bystrye-ssylki')}
                        </p>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {getInfoText(
                                i18n('isklyucheniem-yavlyayutsya-ssylki-na')
                            )}
                        </p>
                    </>
                );

            case ModerationRejectKey.licenseRequiredQl:
                return (
                    <>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('bystrye-ssylki-vedut-na')}
                        </p>
                        {getLicenseHelpText(lang, tld)}
                        {getGuaranteeLetterRecommendation(
                            lang,
                            tld,
                            isOperatorAgency,
                            clientRegion,
                            bid
                        )}
                        {documentsTimeHelp}
                    </>
                );

            case ModerationRejectKey.irrelevantMobileContent:
                return (
                    <p
                        className={Bem.cls(
                            'moderation-reject-reason-description',
                            'par'
                        )}
                    >
                        {i18n('ubedites-chto-ssylka-s')}
                    </p>
                );

            case ModerationRejectKey.irrelevantSiteLinkDescription:
                return (
                    <p
                        className={Bem.cls(
                            'moderation-reject-reason-description',
                            'par'
                        )}
                    >
                        {i18n('opisanie-bystroy-ssylki-dolzhno')}
                    </p>
                );

            case ModerationRejectKey.yandexPolicyCallout:
                return (
                    <p
                        className={Bem.cls(
                            'moderation-reject-reason-description',
                            'par'
                        )}
                    >
                        {i18nRaw('utochnenie-ne-sootvetstvuet-reklamnoy', {
                            link: (
                                <StyledLink
                                    theme={StyledLinkTheme.blue}
                                    href={
                                        getModerationHelpLinks(lang, tld)
                                            .generalAdvRules
                                    }
                                    target={LinkTarget.blank}
                                >
                                    {i18n(
                                        'utochnenie-ne-sootvetstvuet-reklamnoy-param-link'
                                    )}
                                </StyledLink>
                            )
                        })}
                    </p>
                );

            case ModerationRejectKey.errorsCallout:
                return (
                    <>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18nRaw('v-tekste-utochneniya-est', {
                                link: (
                                    <StyledLink
                                        theme={StyledLinkTheme.blue}
                                        href={
                                            getModerationHelpLinks(lang, tld)
                                                .moderationErrorText
                                        }
                                        target={LinkTarget.blank}
                                    >
                                        {i18n(
                                            'v-tekste-utochneniya-est-param-link'
                                        )}
                                    </StyledLink>
                                )
                            })}
                        </p>
                    </>
                );

            case ModerationRejectKey.contactInfoCallout:
                return (
                    <p
                        className={Bem.cls(
                            'moderation-reject-reason-description',
                            'par'
                        )}
                    >
                        {i18nRaw('v-tekste-utochneniya-ukazany', {
                            link: (
                                <StyledLink
                                    theme={StyledLinkTheme.blue}
                                    href={
                                        getModerationHelpLinks(lang, tld)
                                            .moderationContactsInText
                                    }
                                    target={LinkTarget.blank}
                                >
                                    {i18n(
                                        'v-tekste-utochneniya-ukazany-param-link'
                                    )}
                                </StyledLink>
                            )
                        })}
                    </p>
                );

            case ModerationRejectKey.falseInfoCallout:
                return (
                    <p
                        className={Bem.cls(
                            'moderation-reject-reason-description',
                            'par'
                        )}
                    >
                        {i18nRaw('v-tekste-utochneniya-est-147', {
                            link: (
                                <StyledLink
                                    theme={StyledLinkTheme.blue}
                                    href={
                                        getModerationHelpLinks(lang, tld)
                                            .moderationSuperlativeComparativeText
                                    }
                                    target={LinkTarget.blank}
                                >
                                    {i18n(
                                        'v-tekste-utochneniya-est-147-param-link'
                                    )}
                                </StyledLink>
                            )
                        })}
                    </p>
                );

            case ModerationRejectKey.dhrefRequirements:
                return (
                    <>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n(
                                'otobrazhaemaya-ssylka-dolzhna-sootvetstvovat'
                            )}
                            &nbsp;
                            {i18n('v-tekste-otobrazhaemoy-ssylki')}:
                        </p>
                        <ul
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'list'
                            )}
                        >
                            <li>
                                {i18n(
                                    'zhargonnye-grubye-prostorechnye-vyrazheniya'
                                )}
                            </li>
                            <li>{i18n('slova-nabrannye-translitom-ili')}</li>
                            <li>
                                {i18n(
                                    'opechatki-grammaticheskie-i-orfograficheskie'
                                )}
                            </li>
                            <li>{i18n('slova-v-sravnitelnoy-ili')}</li>
                            <li>{i18n('kontaktnye-dannye')}</li>
                        </ul>
                        {getGeneralAdvRulesLink(lang, tld)}
                    </>
                );

            case ModerationRejectKey.licenseRequiredDhref:
                return (
                    <>
                        {getLicenseHelpText(lang, tld)}
                        {getGuaranteeLetterRecommendation(
                            lang,
                            tld,
                            isOperatorAgency,
                            clientRegion,
                            bid
                        )}
                        {documentsTimeHelp}
                    </>
                );

            case ModerationRejectKey.tmDhref:
                return getTmReccommendation(
                    lang,
                    tld,
                    isOperatorAgency,
                    bid,
                    contactEmail
                );

            case ModerationRejectKey.russianLawDhref:
                return getRussianLawReccomendation(lang, tld);

            case ModerationRejectKey.adImageBadText:
                return (
                    <ul
                        className={Bem.cls(
                            'moderation-reject-reason-description',
                            'list'
                        )}
                    >
                        <li>{i18n('tekst-dolzhen-svobodno-vosprinimatsya')}</li>
                        <li>{i18n('vremya-pereklyucheniya-kadrov-na')}</li>
                    </ul>
                );

            case ModerationRejectKey.adImageNoBorder:
                return (
                    <>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('izobrazhenie-obyavleniya-dolzhno-imet')}
                        </p>
                        {getGeneralAdvRulesLink(lang, tld)}
                    </>
                );

            case ModerationRejectKey.adImageBadQuality:
                return (
                    <>
                        <ul
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'list'
                            )}
                        >
                            <li>
                                {i18n(
                                    'izobrazhenie-graficheskogo-obyavleniya-dolzhno'
                                )}
                            </li>
                            <li>{i18n('v-animacii-izobrazheniya-nelzya')}</li>
                        </ul>
                        {getGeneralAdvRulesLink(lang, tld)}
                    </>
                );

            case ModerationRejectKey.adImageNoSource:
                return (
                    <>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('izobrazhenie-obyavleniya-dolzhno-davat')}
                        </p>
                        {getGeneralAdvRulesLink(lang, tld)}
                    </>
                );

            case ModerationRejectKey.adImageNasty:
                return (
                    <>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('nelzya-ispolzovat-izobrazheniya-kotorye')}
                        </p>
                        <ol
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'ol-list'
                            )}
                        >
                            <li>
                                {i18n('mogut-byt-vosprinyaty-neodnoznachno')}
                            </li>
                            <li>
                                {i18n(
                                    'soderzhat-naturalisticheskie-izobrazheniya-bolezney'
                                )}
                            </li>
                        </ol>
                    </>
                );

            case ModerationRejectKey.adImageInterfaceElement:
                return (
                    <>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('graficheskoe-obyavlenie-ne-dolzhno')}
                        </p>
                        <ul
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'list'
                            )}
                        >
                            <li>{i18n('elementy-interfeysa-ili-dizayna')}</li>
                            <li>{i18n('elementy-dizayna-stranic-yandeksa')}</li>
                        </ul>
                        {getGeneralAdvRulesLink(lang, tld)}
                    </>
                );

            case ModerationRejectKey.adImageFinance:
                return (
                    <>
                        <h3
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'sub-title'
                            )}
                        >
                            {i18n('cennye-bumagi')}
                        </h3>
                        <ul
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'list'
                            )}
                        >
                            <li>{i18n('reklama-cennyh-bumag-dolzhna')}</li>
                            <li>{i18n('reklama-emissionnyh-cennyh-bumag')}</li>
                        </ul>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('informaciya-o-predostavitele-uslug')}
                        </p>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {getAdvRulesLink(lang, tld)}
                        </p>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('bankovskie-strahovye-uslugi')}
                        </p>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('reklama-bankovskih-strahovyh-i')}
                        </p>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('informaciya-o-predostavitele-uslug')}
                        </p>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {getAdvRulesLink(lang, tld)}
                        </p>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('upravlenie-akciyami')}
                        </p>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('reklama-po-upravleniyu-akciyami')}
                        </p>
                        <ul
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'list'
                            )}
                        >
                            <li>
                                {i18n('naimenovanie-yuridicheskogo-lica-ili')}
                            </li>
                            <li>
                                {i18n(
                                    'istochnik-informacii-podlezhashchey-raskrytiyu'
                                )}
                            </li>
                            <li>{i18n('svedeniya-o-nomere-telefona')}</li>
                        </ul>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('informaciya-o-predostavitele-uslug')}
                        </p>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {getAdvRulesLink(lang, tld)}
                        </p>
                    </>
                );

            case ModerationRejectKey.adImageLottery:
                return (
                    <>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('ubedites-chto-graficheskoe-obyavlenie-119')}
                        </p>
                        <ul
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'list'
                            )}
                        >
                            <li>{i18n('sroki-provedeniya-meropriyatiya')}</li>
                            <li>{i18n('istochnik-gde-mozhno-oznakomitsya')}</li>
                        </ul>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            <StyledLink
                                theme={StyledLinkTheme.blue}
                                href={
                                    getModerationHelpLinks(lang, tld).advRules
                                }
                                target={LinkTarget.blank}
                            >
                                {i18n('polnyy-spisok-trebovaniy')}
                            </StyledLink>
                        </p>
                    </>
                );

            case ModerationRejectKey.adImageShareBuilding:
                return (
                    <p
                        className={Bem.cls(
                            'moderation-reject-reason-description',
                            'par'
                        )}
                    >
                        {i18n('reklama-dolevogo-stroitelstva-dolzhna')}
                    </p>
                );

            case ModerationRejectKey.adImageDistance:
                return (
                    <>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('graficheskoe-obyavlenie-o-distancionnoy')}
                        </p>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            <StyledLink
                                theme={StyledLinkTheme.blue}
                                href={
                                    getModerationHelpLinks(lang, tld).advRules
                                }
                                target={LinkTarget.blank}
                            >
                                {i18n('polnyy-spisok-trebovaniy')}
                            </StyledLink>
                        </p>
                    </>
                );

            case ModerationRejectKey.adImageMedicine:
                return (
                    <>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('graficheskoe-obyavlenie-o-lekarstvennyh')}
                        </p>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('graficheskoe-obyavlenie-o-bad')}
                        </p>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {displayRulesLink(lang, tld)}
                        </p>
                    </>
                );

            case ModerationRejectKey.adImageSmi:
                return (
                    <>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('v-sootvetstvii-s-chastyu')}
                        </p>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('k-takoy-informacionnoy-produkcii')}
                        </p>
                        <ul
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'list'
                            )}
                        >
                            <li>{i18n('bazy-dannyh-i-programmnoe')}</li>
                            <li>{i18n('filmy-igry-muzyka')}</li>
                            <li>
                                {i18n('koncerty-vystavki-festivali-spektakli')}
                            </li>
                            <li>{i18n('pechatnye-i-internet-izdaniya')}</li>
                            <li>{i18n('konkretnye-programmy-i-peredachi')}</li>
                        </ul>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {displayRulesLink(lang, tld)}
                        </p>
                    </>
                );

            case ModerationRejectKey.adImageBabyfood:
                return (
                    <>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('graficheskoe-obyavlenie-o-produktah')}
                        </p>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {displayRulesLink(lang, tld)}
                        </p>
                    </>
                );

            case ModerationRejectKey.adImageHouseCooperative:
                return (
                    <>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('graficheskoe-obyavlenie-o-zhilishchno')}
                        </p>

                        <ul
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'list'
                            )}
                        >
                            <li>
                                {i18n('naimenovanie-yuridicheskogo-lica-fio')}
                            </li>
                            <li>{i18n('informaciyu-o-poryadke-pokrytiya')}</li>
                            <li>{i18n('svedeniya-o-vklyuchenii-zhnk')}</li>
                            <li>{i18n('adres-sayta-na-kotorom')}</li>
                        </ul>
                    </>
                );

            case ModerationRejectKey.videoSmi:
                return (
                    <>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('v-obyavleniyah-s-targetingom')}
                        </p>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('k-takoy-informacionnoy-produkcii')}
                        </p>
                        <ul
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'list'
                            )}
                        >
                            <li>{i18n('bazy-dannyh-i-programmnoe-148')}</li>
                            <li>{i18n('filmy-igry-muzyka-149')}</li>
                            <li>
                                {i18n(
                                    'koncerty-vystavki-festivali-spektakli-150'
                                )}
                            </li>
                            <li>{i18n('pechatnye-i-internet-izdaniya-151')}</li>
                            <li>
                                {i18n('konkretnye-programmy-i-peredachi-152')}
                            </li>
                        </ul>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('vremya-pokaza-vozrastnoy-kategorii')}
                        </p>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {getAdvRulesLink(lang, tld)}
                        </p>
                    </>
                );

            case ModerationRejectKey.videoMedicine:
                return (
                    <>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('videorolik-o-lekarstvennyh-sredstvah')}
                        </p>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('videorolik-s-reklamoy-bad')}
                        </p>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {getAdvRulesLink(lang, tld)}
                        </p>
                    </>
                );

            case ModerationRejectKey.videoHouseCooperative:
                return (
                    <>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('videorolik-s-reklamoy-zhilishchno')}
                        </p>
                        <ul
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'list'
                            )}
                        >
                            <li>
                                {i18n(
                                    'naimenovanie-yuridicheskogo-lica-fio-154'
                                )}
                            </li>
                            <li>
                                {i18n('informaciyu-o-poryadke-pokrytiya-155')}
                            </li>
                            <li>{i18n('svedeniya-o-vklyuchenii-zhnk-156')}</li>
                            <li>{i18n('adres-sayta-na-kotorom-157')}</li>
                        </ul>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('vremya-pokaza-dannoy-informacii')}
                        </p>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {getGeneralAdvRulesLink(lang, tld)}
                        </p>
                    </>
                );

            case ModerationRejectKey.videoLottery:
                return (
                    <>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('videorolik-s-reklamoy-stimuliruyushchego')}
                        </p>
                        <ul
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'list'
                            )}
                        >
                            <li>
                                {i18n('sroki-provedeniya-meropriyatiya-159')}
                            </li>
                            <li>
                                {i18n('istochnik-gde-mozhno-oznakomitsya-160')}
                            </li>
                        </ul>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {getAdvRulesLink(lang, tld)}
                        </p>
                    </>
                );

            case ModerationRejectKey.videoShareBuilding:
                return (
                    <>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('reklama-dolevogo-stroitelstva-dolzhna')}
                        </p>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {getAdvRulesLink(lang, tld)}
                        </p>
                    </>
                );

            case ModerationRejectKey.videoFinance:
                return (
                    <>
                        <Collapser
                            cls={Bem.cls(
                                'moderation-reject-reason-description',
                                'collapser'
                            )}
                            items={[
                                {
                                    title: i18n('cennye-bumagi'),
                                    key: '1',
                                    content: (
                                        <>
                                            <ul
                                                className={Bem.cls(
                                                    'moderation-reject-reason-description',
                                                    'list'
                                                )}
                                            >
                                                <li>
                                                    {i18n(
                                                        'reklama-cennyh-bumag-dolzhna-163'
                                                    )}
                                                </li>
                                                <li>
                                                    {i18n(
                                                        'reklama-emissionnyh-cennyh-bumag-164'
                                                    )}
                                                </li>
                                            </ul>
                                            <p
                                                className={Bem.cls(
                                                    'moderation-reject-reason-description',
                                                    'par'
                                                )}
                                            >
                                                {i18n(
                                                    'informaciya-o-predostavitele-uslug'
                                                )}
                                            </p>
                                            <p
                                                className={Bem.cls(
                                                    'moderation-reject-reason-description',
                                                    'par'
                                                )}
                                            >
                                                {getAdvRulesLink(lang, tld)}
                                            </p>
                                        </>
                                    )
                                },
                                {
                                    title: i18n('bankovskie-strahovye-uslugi'),
                                    key: '2',
                                    content: (
                                        <>
                                            <p
                                                className={Bem.cls(
                                                    'moderation-reject-reason-description',
                                                    'par'
                                                )}
                                            >
                                                {i18n(
                                                    'reklama-bankovskih-strahovyh-i'
                                                )}
                                            </p>

                                            <p
                                                className={Bem.cls(
                                                    'moderation-reject-reason-description',
                                                    'par'
                                                )}
                                            >
                                                {i18n(
                                                    'informaciya-o-predostavitele-uslug'
                                                )}
                                            </p>

                                            <p
                                                className={Bem.cls(
                                                    'moderation-reject-reason-description',
                                                    'par'
                                                )}
                                            >
                                                {getAdvRulesLink(lang, tld)}
                                            </p>
                                        </>
                                    )
                                },
                                {
                                    title: i18n('upravlenie-akciyami'),
                                    key: '3',
                                    content: (
                                        <>
                                            <p
                                                className={Bem.cls(
                                                    'moderation-reject-reason-description',
                                                    'par'
                                                )}
                                            >
                                                {i18n(
                                                    'reklama-po-upravleniyu-akciyami'
                                                )}
                                            </p>

                                            <ul
                                                className={Bem.cls(
                                                    'moderation-reject-reason-description',
                                                    'list'
                                                )}
                                            >
                                                <li>
                                                    {i18n(
                                                        'naimenovanie-yuridicheskogo-lica-ili-165'
                                                    )}
                                                </li>
                                                <li>
                                                    {i18n(
                                                        'istochnik-informacii-podlezhashchey-raskrytiyu-166'
                                                    )}
                                                </li>
                                                <li>
                                                    {i18n(
                                                        'svedeniya-o-nomere-telefona-167'
                                                    )}
                                                </li>
                                            </ul>

                                            <p
                                                className={Bem.cls(
                                                    'moderation-reject-reason-description',
                                                    'par'
                                                )}
                                            >
                                                {i18n(
                                                    'informaciya-o-predostavitele-uslug'
                                                )}
                                            </p>

                                            <p
                                                className={Bem.cls(
                                                    'moderation-reject-reason-description',
                                                    'par'
                                                )}
                                            >
                                                {getAdvRulesLink(lang, tld)}
                                            </p>
                                        </>
                                    )
                                }
                            ]}
                        />
                    </>
                );

            case ModerationRejectKey.videoDistanceSales:
                return (
                    <>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('videorolik-s-reklamoy-distancionnoy')}
                        </p>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {getAdvRulesLink(lang, tld)}
                        </p>
                    </>
                );

            case ModerationRejectKey.videoBabyfood:
                return (
                    <>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('videorolik-s-reklamoy-produktov')}
                        </p>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {getAdvRulesLink(lang, tld)}
                        </p>
                    </>
                );

            case ModerationRejectKey.videoRequirements:
                return (
                    <>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('videorolik-dolzhen-byt-kachestvennym')}
                        </p>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('videorolik-ne-dolzhen-soderzhat')}
                        </p>
                        <ul
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'list'
                            )}
                        >
                            <li>
                                {i18n('elementy-interfeysa-ili-dizayna-170')}
                            </li>
                            <li>{i18n('obrazy-kotorye-ne-sootvetstvuyut')}</li>
                            <li>{i18n('izobrazheniya-kotorye-mogut-byt')}</li>
                            <li>
                                {i18n(
                                    'naturalisticheskie-izobrazheniya-bolezney-medicinskih'
                                )}
                            </li>
                        </ul>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {getGeneralAdvRulesLink(lang, tld)}
                        </p>
                    </>
                );

            case ModerationRejectKey.audioRequirements:
                return (
                    <>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('proverte-chto-audiorolik')}
                        </p>
                        <ol
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'ol-list'
                            )}
                        >
                            <li>
                                {i18n('gramotnyy-kachestvennyy-i-komfortnyy')}
                            </li>
                            <li>
                                {i18n(
                                    'sootvetstvuet-zagolovku-tekstu-obyavleniya'
                                )}
                            </li>
                            <li>{i18n('sinhronnyy-s-videorolikom')}</li>
                        </ol>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('ne-ispolzuyte-v-audiorolike')}
                        </p>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {getGeneralAdvRulesLink(lang, tld)}
                        </p>
                    </>
                );

            case ModerationRejectKey.turbolandingRequirements:
                return (
                    <>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('turbolanding-requirements-1')}
                        </p>
                        <ol
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'ol-list'
                            )}
                        >
                            <li>{i18n('turbolanding-requirements-2')}</li>
                            <li>
                                {i18nRaw('turbolanding-requirements-3', {
                                    'special-categories-link': (
                                        <StyledLink
                                            theme={StyledLinkTheme.blue}
                                            href={
                                                getModerationHelpLinks(
                                                    lang,
                                                    tld
                                                ).moderationRestrictedCategories
                                            }
                                            target={LinkTarget.blank}
                                        >
                                            {i18n(
                                                'special-categories-link-turbo'
                                            )}
                                        </StyledLink>
                                    )
                                })}
                            </li>
                            <li>{i18n('turbolanding-requirements-4')}</li>
                            <li>{i18n('turbolanding-requirements-5')}</li>
                        </ol>
                    </>
                );

            case ModerationRejectKey.turbolandingRequirementsSlink:
                return (
                    <>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n('turbolanding-requirements-slink-1')}
                        </p>

                        <ol
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'ol-list'
                            )}
                        >
                            <li>{i18n('turbolanding-requirements-slink-2')}</li>
                            <li>
                                {i18nRaw('turbolanding-requirements-slink-3', {
                                    'special-categories-link': (
                                        <StyledLink
                                            theme={StyledLinkTheme.blue}
                                            href={
                                                getModerationHelpLinks(
                                                    lang,
                                                    tld
                                                ).moderationRestrictedCategories
                                            }
                                            target={LinkTarget.blank}
                                        >
                                            {i18n(
                                                'special-categories-link-turbo'
                                            )}
                                        </StyledLink>
                                    )
                                })}
                            </li>
                            <li>{i18n('turbolanding-requirements-slink-4')}</li>
                            <li>{i18n('turbolanding-requirements-slink-5')}</li>
                        </ol>
                    </>
                );

            case ModerationRejectKey.html5Nasty:
                return (
                    <p
                        className={Bem.cls(
                            'moderation-reject-reason-description',
                            'par'
                        )}
                    >
                        {i18n('html5-nasty-recommendation')}
                    </p>
                );

            case ModerationRejectKey.html5Babyfood:
                return (
                    <p
                        className={Bem.cls(
                            'moderation-reject-reason-description',
                            'par'
                        )}
                    >
                        {i18n('html5-babyfood-recommendation')}
                    </p>
                );

            case ModerationRejectKey.html5Finance:
                return (
                    <p
                        className={Bem.cls(
                            'moderation-reject-reason-description',
                            'par'
                        )}
                    >
                        {i18n('html5-finance-recommendation')}
                    </p>
                );

            case ModerationRejectKey.html5Funds:
                return (
                    <p
                        className={Bem.cls(
                            'moderation-reject-reason-description',
                            'par'
                        )}
                    >
                        {i18n('html5-funds-recommendation')}
                    </p>
                );

            case ModerationRejectKey.html5BadQuality:
                return (
                    <p
                        className={Bem.cls(
                            'moderation-reject-reason-description',
                            'par'
                        )}
                    >
                        {i18n('html5-bad-quality-recommendation')}
                    </p>
                );

            case ModerationRejectKey.html5RussianLaw:
                return (
                    <p
                        className={Bem.cls(
                            'moderation-reject-reason-description',
                            'par'
                        )}
                    >
                        {i18nRaw('html5-russian-law-recommendation', {
                            link: (
                                <StyledLink
                                    theme={StyledLinkTheme.blue}
                                    href={
                                        getModerationHelpLinks(lang, tld)
                                            .moderationLaw
                                    }
                                    target={LinkTarget.blank}
                                >
                                    {i18n(
                                        'v-tekste-utochneniya-est-147-param-link'
                                    )}
                                </StyledLink>
                            )
                        })}
                    </p>
                );

            case ModerationRejectKey.html5Illness:
                return (
                    <p
                        className={Bem.cls(
                            'moderation-reject-reason-description',
                            'par'
                        )}
                    >
                        {i18n('html5-illness-recommendation')}
                    </p>
                );

            case ModerationRejectKey.html5Spam:
                return (
                    <p
                        className={Bem.cls(
                            'moderation-reject-reason-description',
                            'par'
                        )}
                    >
                        {i18nRaw('html5-spam-recommendation', {
                            link: (
                                <StyledLink
                                    theme={StyledLinkTheme.blue}
                                    href={
                                        getModerationHelpLinks(lang, tld)
                                            .moderationNoComments
                                    }
                                    target={LinkTarget.blank}
                                >
                                    {i18n(
                                        'v-tekste-utochneniya-est-147-param-link'
                                    )}
                                </StyledLink>
                            )
                        })}
                    </p>
                );

            case ModerationRejectKey.html5HouseCooperative:
                return (
                    <p
                        className={Bem.cls(
                            'moderation-reject-reason-description',
                            'par'
                        )}
                    >
                        {i18n('html5-house-cooperative-recommendation')}
                    </p>
                );

            case ModerationRejectKey.html5InterfaceElement:
                return (
                    <p
                        className={Bem.cls(
                            'moderation-reject-reason-description',
                            'par'
                        )}
                    >
                        {i18n('html5-interface-element-recommendation')}
                    </p>
                );

            case ModerationRejectKey.html5NoBorder:
                return (
                    <p
                        className={Bem.cls(
                            'moderation-reject-reason-description',
                            'par'
                        )}
                    >
                        {i18n('html5-no-border-recommendation')}
                    </p>
                );

            case ModerationRejectKey.html5HrefsErrors:
                return (
                    <>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            {i18n(
                                'html5-hrefs-errors-recommendation-list-title'
                            )}
                            :
                        </p>
                        <ul
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'list'
                            )}
                        >
                            <li>
                                {i18n(
                                    'html5-hrefs-errors-recommendation-list-item1'
                                )}
                            </li>
                            <li>
                                {i18n(
                                    'html5-hrefs-errors-recommendation-list-item2'
                                )}

                                <code
                                    className={Bem.cls(
                                        'moderation-reject-reason-description',
                                        'code'
                                    )}
                                >
                                    {
                                        '<a id="click_area" href="#" target="_blank">...</a>'
                                    }
                                    <br />
                                    {'// after the a element'}
                                    <br />
                                    {
                                        '// assign them the href attribute values as follows:'
                                    }
                                    <br />
                                    {'<script>'}
                                    <br />
                                    &nbsp;&nbsp;
                                    {
                                        'document.getElementById("click_area").href = yandexHTML5BannerApi.getClickURLNum(1);'
                                    }
                                    <br />
                                    {'</script>'}
                                </code>
                            </li>
                        </ul>
                        <p
                            className={Bem.cls(
                                'moderation-reject-reason-description',
                                'par'
                            )}
                        >
                            <StyledLink
                                theme={StyledLinkTheme.blue}
                                href={
                                    getModerationHelpLinks(lang, tld)
                                        .html5Requirements
                                }
                                target={LinkTarget.blank}
                            >
                                {i18n('html5-requirements')}
                            </StyledLink>
                        </p>
                    </>
                );

            case ModerationRejectKey.html5NoSource:
                return (
                    <p
                        className={Bem.cls(
                            'moderation-reject-reason-description',
                            'par'
                        )}
                    >
                        {i18n('html5-no-source-recommendation')}
                    </p>
                );

            case ModerationRejectKey.html5Smi:
                return (
                    <p
                        className={Bem.cls(
                            'moderation-reject-reason-description',
                            'par'
                        )}
                    >
                        {i18n('html5-smi-recommendation')}
                    </p>
                );

            case ModerationRejectKey.html5Securities:
                return (
                    <p
                        className={Bem.cls(
                            'moderation-reject-reason-description',
                            'par'
                        )}
                    >
                        {i18n('html5-securities-recommendation')}
                    </p>
                );

            case ModerationRejectKey.html5Lottery:
                return (
                    <p
                        className={Bem.cls(
                            'moderation-reject-reason-description',
                            'par'
                        )}
                    >
                        {i18n('html5-lottery-recommendation')}
                    </p>
                );

            case ModerationRejectKey.html5ShareBuilding:
                return (
                    <p
                        className={Bem.cls(
                            'moderation-reject-reason-description',
                            'par'
                        )}
                    >
                        {i18n('html5-share-building-recommendation')}
                    </p>
                );

            case ModerationRejectKey.html5BadText:
                return (
                    <p
                        className={Bem.cls(
                            'moderation-reject-reason-description',
                            'par'
                        )}
                    >
                        {i18n('html5-bad-text-recommendation')}
                    </p>
                );

            case ModerationRejectKey.html5Medicine:
                return (
                    <p
                        className={Bem.cls(
                            'moderation-reject-reason-description',
                            'par'
                        )}
                    >
                        {i18n('html5-medicine-recommendation')}
                    </p>
                );

            default:
                return '';
        }
    };
}

const getModerationHelpLinks = (lang: Lang, tld: Tld) => {
    return {
        restrictedGoodsInKz: 'https://legal.yandex.kz/adv_rules/?ncrnd=5933',
        guarantyLetter: UrlHelpers.getCommonHelpUrl(
            'direct/moderation/letter-of-guarantee.html',
            tld
        ),
        advRules: UrlHelpers.getLegalUrl('adv_rules', tld, lang),
        generalAdvRules: UrlHelpers.getLegalUrl('general_adv_rules', tld, lang),
        directAdvRules: UrlHelpers.getLegalUrl('direct_adv_rules', tld, lang),
        directDisplayRules: UrlHelpers.getLegalUrl(
            'direct_display_rules',
            tld,
            lang
        ),
        vcardRules: UrlHelpers.getCommonHelpUrl(
            'direct/features/vcards.xml',
            tld
        ),
        errorBan: UrlHelpers.getCommonHelpUrl(
            'direct/troubleshooting/error-ban.xml',
            tld
        ),
        documentsForModeration: UrlHelpers.getCommonHelpUrl(
            'direct/troubleshooting/moderation.html#document',
            tld
        ),
        quickLinksRules: UrlHelpers.getCommonHelpUrl(
            'direct-tooltips/quick-links.xml',
            tld
        ),
        moderationErrorText: UrlHelpers.getCommonHelpUrl(
            'direct-tooltips/moderation/errors-text.xml',
            tld
        ),
        moderationContactsInText: UrlHelpers.getCommonHelpUrl(
            'direct-tooltips/moderation/contacts-in-text.xml',
            tld
        ),
        moderationSuperlativeComparativeText: UrlHelpers.getCommonHelpUrl(
            'direct-tooltips/moderation/superlative-comparative-text.xml',
            tld
        ),
        moderationRestrictedCategories: UrlHelpers.getCommonHelpUrl(
            'direct/moderation/restricted-categories.html',
            tld
        ),
        moderationSpecialCategories: UrlHelpers.getCommonHelpUrl(
            'direct/moderation/special-categories.html',
            tld
        ),
        moderationLaw: UrlHelpers.getCommonHelpUrl(
            'direct-tooltips/moderation/law.html',
            tld
        ),
        moderationNoComments: UrlHelpers.getCommonHelpUrl(
            'direct-tooltips/moderation/no-comments.html',
            tld
        ),
        chooseKeywordsInfo: UrlHelpers.getCommonHelpUrl(
            'direct/quick-start/choose-keywords.xml',
            tld
        ),
        html5Requirements: UrlHelpers.getAdvUrl(
            'requirements/html5#direct',
            tld
        )
    };
};

const getRussianLawRecommendation = (lang: Lang, tld: Tld) => {
    return (
        <>
            <p
                className={Bem.cls(
                    'moderation-reject-reason-description',
                    'par'
                )}
            >
                {i18n('nelzya-reklamirovat')}
            </p>
            <ol
                className={Bem.cls(
                    'moderation-reject-reason-description',
                    'ol-list'
                )}
            >
                <li>{i18n('azartnye-igry-sportivnye-prognozy')}</li>
                <li>{i18n('alkogolnuyu-produkciyu')}</li>
                <li>{i18n('tabachnuyu-produkciyu-i-nbsp')}</li>
                <li>{i18n('oruzhie')}</li>
                <li>{i18n('recepturnye-preparaty')}</li>
                <li>{i18n('deystviya-pobuzhdayushchie-k-nbsp')}</li>
                <li>
                    {i18n('distancionnuyu-prodazhu-tovarov-zapreshchyonnyh')}
                </li>
                <li>{i18n('prodazhu-i-nbsp-poddelku')}</li>
                <li>
                    {i18nRaw('drugie-link-tovary-i', {
                        link: (
                            <StyledLink
                                theme={StyledLinkTheme.blue}
                                href={
                                    getModerationHelpLinks(lang, tld).advRules
                                }
                                target={LinkTarget.blank}
                            >
                                {i18n('drugie-link-tovary-i-param-link')}
                            </StyledLink>
                        )
                    })}
                </li>
            </ol>
        </>
    );
};

const getTmReccommendation = (
    lang: Lang,
    tld: Tld,
    isOperatorAgency: boolean,
    bid?: string,
    contactEmail?: string
) => {
    return (
        <>
            <p
                className={Bem.cls(
                    'moderation-reject-reason-description',
                    'par'
                )}
            >
                {i18n('my-poluchili-oficialnuyu-zhalobu')}
            </p>
            <p
                className={Bem.cls(
                    'moderation-reject-reason-description',
                    'par'
                )}
            >
                {i18nRaw('pered-zapolneniem-garantiynogo-pisma', {
                    rules: (
                        <StyledLink
                            theme={StyledLinkTheme.blue}
                            href={
                                getModerationHelpLinks(lang, tld).guarantyLetter
                            }
                            target={LinkTarget.blank}
                        >
                            {i18n(
                                'pered-zapolneniem-garantiynogo-pisma-param-rules'
                            )}
                        </StyledLink>
                    )
                })}
            </p>

            {getDownloadGuaranteeLetterButton(isOperatorAgency, bid)}

            <p
                className={Bem.cls(
                    'moderation-reject-reason-description',
                    'par'
                )}
            >
                {getInfoText(
                    i18nRaw('dlya-proverki-korrektnosti-zapolneniya', {
                        send: (
                            <StyledLink
                                theme={StyledLinkTheme.blue}
                                href={getSendDocsUrl(lang, tld, contactEmail)}
                                target={LinkTarget.blank}
                            >
                                {i18n(
                                    'dlya-proverki-korrektnosti-zapolneniya-param-send'
                                )}
                            </StyledLink>
                        )
                    })
                )}
            </p>
        </>
    );
};

const getRussianLawReccomendation = (lang: Lang, tld: Tld) => {
    return (
        <>
            <p
                className={Bem.cls(
                    'moderation-reject-reason-description',
                    'par'
                )}
            >
                {i18n('nelzya-reklamirovat')}
            </p>
            <ul
                className={Bem.cls(
                    'moderation-reject-reason-description',
                    'list'
                )}
            >
                <li>{i18n('azartnye-igry-sportivnye-prognozy')}</li>
                <li>{i18n('alkogolnuyu-produkciyu')}</li>
                <li>{i18n('tabachnuyu-produkciyu-i-nbsp')}</li>
                <li>{i18n('oruzhie')}</li>
                <li>{i18n('recepturnye-preparaty')}</li>
                <li>{i18n('deystviya-pobuzhdayushchie-k-nbsp')}</li>
                <li>
                    {i18n('distancionnuyu-prodazhu-tovarov-zapreshchyonnyh')}
                </li>
                <li>{i18n('prodazhu-i-nbsp-poddelku')}</li>
                <li>
                    {i18nRaw('drugie-link-tovary-i', {
                        link: (
                            <StyledLink
                                theme={StyledLinkTheme.blue}
                                href={
                                    getModerationHelpLinks(lang, tld).advRules
                                }
                                target={LinkTarget.blank}
                            >
                                {i18n('drugie-link-tovary-i-param-link')}
                            </StyledLink>
                        )
                    })}
                </li>
            </ul>
        </>
    );
};

const getLicenseHelpText = (lang: Lang, tld: Tld) => {
    return (
        <p className={Bem.cls('moderation-reject-reason-description', 'par')}>
            {i18nRaw('komplekt-dokumentov-zavisit-ot', {
                help: (
                    <StyledLink
                        theme={StyledLinkTheme.blue}
                        href={
                            getModerationHelpLinks(lang, tld)
                                .moderationSpecialCategories
                        }
                        target={LinkTarget.blank}
                    >
                        {i18n('komplekt-dokumentov-zavisit-ot-param-help')}
                    </StyledLink>
                )
            })}
        </p>
    );
};

const getGuaranteeLetterRecommendation = (
    lang: Lang,
    tld: Tld,
    isOperatorAgency: boolean,
    clientRegion: string,
    bid?: string
) => {
    return (
        <>
            <p
                className={Bem.cls(
                    'moderation-reject-reason-description',
                    'par'
                )}
            >
                {i18nRaw('esli-dlya-vashih-tovarov', {
                    rules: (
                        <StyledLink
                            theme={StyledLinkTheme.blue}
                            href={
                                getModerationHelpLinks(lang, tld).guarantyLetter
                            }
                            target={LinkTarget.blank}
                        >
                            {i18n('esli-dlya-vashih-tovarov-param-rules')}
                        </StyledLink>
                    )
                })}
            </p>
            {getDownloadGuaranteeLetterSelect(
                isOperatorAgency,
                clientRegion,
                bid
            )}
        </>
    );
};

const documentsTimeHelp = (
    <p className={Bem.cls('moderation-reject-reason-description', 'par')}>
        {getInfoText(i18n('proverka-dokumentov-mozhet-zanimat'))}
    </p>
);

const getSendDocsUrl = (
    lang: Lang,
    tld: Tld,
    contactEmail?: string
): string => {
    if (contactEmail) {
        return `mailto:${contactEmail}`;
    }

    return getModerationHelpLinks(lang, tld).documentsForModeration;
};

const getGeneralAdvRulesLink = (lang: Lang, tld: Tld) => {
    return (
        <StyledLink
            theme={StyledLinkTheme.blue}
            href={getModerationHelpLinks(lang, tld).generalAdvRules}
            target={LinkTarget.blank}
        >
            {i18n('polnyy-spisok-trebovaniy')}
        </StyledLink>
    );
};

const getAdvRulesLink = (lang: Lang, tld: Tld) => {
    return (
        <StyledLink
            theme={StyledLinkTheme.blue}
            href={getModerationHelpLinks(lang, tld).advRules}
            target={LinkTarget.blank}
        >
            {i18n('pravila-razmeshcheniya-reklamy')}
        </StyledLink>
    );
};

const displayRulesLink = (lang: Lang, tld: Tld) => {
    return (
        <StyledLink
            theme={StyledLinkTheme.blue}
            href={getModerationHelpLinks(lang, tld).directDisplayRules}
            target={LinkTarget.blank}
        >
            {i18n('pravila-pokaza')}
        </StyledLink>
    );
};

function getInfoText(
    text:
        | Array<JSX.Element | string | number | object | undefined>
        | string
        | JSX.Element
): JSX.Element {
    return (
        <>
            <Shape2
                width={12}
                height={12}
                icon={Shape2Icon.info}
                theme={Shape2Theme.light}
                cls={Bem.cls(
                    'moderation-reject-reason-description',
                    'text-icon'
                )}
            />
            {text}
        </>
    );
}

enum GuaranteeLetterTopic {
    weapons = '6',
    pyrotechnics = '8',
    intellectualProperty = '4',
    notMedicine = '5',
    realtors = '12',
    general = '1'
}

const guaranteeLetterTopicsTitle: Record<GuaranteeLetterTopic, string> = {
    [GuaranteeLetterTopic.weapons]: i18n('weapons-topic'),
    [GuaranteeLetterTopic.pyrotechnics]: i18n('pyrotechnics-topic'),
    [GuaranteeLetterTopic.intellectualProperty]: i18n(
        'intellectual-property-topic'
    ),
    [GuaranteeLetterTopic.notMedicine]: i18n('not-medicine-topic'),
    [GuaranteeLetterTopic.realtors]: i18n('realtors-topic'),
    [GuaranteeLetterTopic.general]: i18n('general-topic')
};

function getDownloadGuaranteeLetterButton(
    isOperatorAgency: boolean,
    bid?: string
): JSX.Element | null {
    if (bid === undefined) {
        return null;
    }

    return (
        <ButtonDecorator
            text={i18n('download')}
            theme={'pseudo'}
            size={'s'}
            type={'link'}
            target={LinkTarget.blank}
            url={UrlHelpers.getGuaranteeLetterDownloadUrl(
                bid,
                GuaranteeLetterTopic.intellectualProperty,
                isOperatorAgency
            )}
        />
    );
}

function getDownloadGuaranteeLetterSelect(
    isOperatorAgency: boolean,
    clientRegion: string,
    bid?: string
): JSX.Element | null {
    if (bid === undefined) {
        return null;
    }

    const topics = [
        GuaranteeLetterTopic.weapons,
        GuaranteeLetterTopic.pyrotechnics,
        GuaranteeLetterTopic.notMedicine,
        ['159', '149'].includes(clientRegion)
            ? GuaranteeLetterTopic.realtors
            : undefined,
        GuaranteeLetterTopic.general
    ].filter(topic => topic !== undefined);

    return (
        <Dropdown
            theme={'normal'}
            size={'s'}
            width={'fixed'}
            hasTick={true}
            switcher={'button'}
        >
            <Dropdown.Switcher>{i18n('download')}</Dropdown.Switcher>
            <Dropdown.Popup>
                <Menu
                    theme={'normal'}
                    cls={Bem.cls('inline-title-selector', 'menu')}
                    items={topics.map((topic: GuaranteeLetterTopic) => {
                        return {
                            text: guaranteeLetterTopicsTitle[topic],
                            url: UrlHelpers.getGuaranteeLetterDownloadUrl(
                                bid,
                                topic,
                                isOperatorAgency
                            )
                        };
                    })}
                    type={'navigation'}
                    size={'s'}
                />
            </Dropdown.Popup>
        </Dropdown>
    );
}
